import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Logo, ThemeToggle } from "@/components/AppShell";
import { toast } from "sonner";
import { Loader2, TrendingUp, LayoutGrid, Share2 } from "lucide-react";

const GOOGLE_ENABLED = Boolean(process.env.REACT_APP_GOOGLE_CLIENT_ID);

// Custom Google Sign-In: opens a small popup that closes itself once auth completes,
// no separate branded page. REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Only mounted when a client id is configured - useGoogleLogin throws "Missing required
// parameter client_id" on mount otherwise.
function GoogleAuthButton() {
  const { googleLogin } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const onClick = useGoogleLogin({
    flow: "auth-code",
    scope: "openid email profile",
    onSuccess: async ({ code }) => {
      setBusy(true);
      try {
        await googleLogin(code);
        navigate("/app");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Google sign-in failed");
      } finally {
        setBusy(false);
      }
    },
    onError: () => {
      toast.error("Google sign-in failed");
      setBusy(false);
    },
  });

  return (
    <Button type="button" variant="outline" className="w-full" onClick={onClick} disabled={busy} data-testid="google-login-button">
      {busy ? (
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      ) : (
        <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18A10.96 10.96 0 0 0 1 12c0 1.77.43 3.45 1.18 4.94l3.66-2.84z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.16-3.16C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
        </svg>
      )}
      Continue with Google
    </Button>
  );
}

export default function AuthPage() {
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({ name: "", email: "", password: "" });


  if (user) {
    return <Navigate to="/app" replace />;
  }

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(loginForm.email, loginForm.password);
      navigate("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(regForm.email, regForm.password, regForm.name);
      navigate("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* left brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-[#0A2540] p-10 lg:flex">
        <div className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(900px 500px at 20% 10%, rgba(56,189,248,0.18), transparent 60%), radial-gradient(700px 400px at 80% 90%, rgba(20,184,166,0.14), transparent 55%)" }} />
        <Link to="/" className="relative z-10 text-white">
          <Logo className="[&_span:last-child]:text-white" />
        </Link>
        <div className="relative z-10 max-w-md">
          <h2 className="font-display text-3xl font-semibold leading-tight text-white">
            Your trading, your dashboard. Built exactly the way you think.
          </h2>
          <div className="mt-8 space-y-4">
            {[
              { icon: LayoutGrid, text: "Drag, drop and resize 24 analytics widgets" },
              { icon: TrendingUp, text: "Stats that go deep: drawdown, profit factor, expectancy" },
              { icon: Share2, text: "Share read-only workspaces with investors & clients" },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-slate-300">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10">
                  <f.icon className="h-4 w-4 text-sky-300" />
                </span>
                {f.text}
              </div>
            ))}
          </div>
        </div>
        <p className="relative z-10 text-xs text-slate-400">Premium trading analytics · MT4 & MT5</p>
      </div>

      {/* right form */}
      <div className="flex w-full flex-col lg:w-1/2">
        <div className="flex items-center justify-between p-4">
          <Link to="/" className="lg:invisible"><Logo /></Link>
          <ThemeToggle />
        </div>
        <div className="flex flex-1 items-center justify-center px-4 pb-16">
          <div className="w-full max-w-sm">
            <h1 className="font-display text-2xl font-semibold tracking-tight">Welcome</h1>
            <p className="mt-1 text-sm text-muted-foreground">Sign in to your workspace, or create a free account.</p>
            <Tabs defaultValue="login" className="mt-6">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login" data-testid="tab-login">Sign in</TabsTrigger>
                <TabsTrigger value="register" data-testid="tab-register">Create account</TabsTrigger>
              </TabsList>
              <TabsContent value="login" className="mt-5 space-y-4">
                {GOOGLE_ENABLED && (
                  <>
                    <GoogleAuthButton />
                    <div className="flex items-center gap-3">
                      <Separator className="flex-1" />
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">or</span>
                      <Separator className="flex-1" />
                    </div>
                  </>
                )}
                <form onSubmit={doLogin} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="login-email">Email</Label>
                    <Input id="login-email" type="email" required value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      placeholder="you@example.com" data-testid="login-email-input" />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="login-password">Password</Label>
                      <Link to="/forgot-password" className="text-xs font-medium text-primary hover:underline" data-testid="login-forgot-password-link">
                        Forgot password?
                      </Link>
                    </div>
                    <Input id="login-password" type="password" required value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      placeholder="••••••••" data-testid="login-password-input" />
                  </div>
                  <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit-button">
                    {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Sign in
                  </Button>
                </form>
              </TabsContent>
              <TabsContent value="register" className="mt-5 space-y-4">
                {GOOGLE_ENABLED && (
                  <>
                    <GoogleAuthButton />
                    <div className="flex items-center gap-3">
                      <Separator className="flex-1" />
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">or</span>
                      <Separator className="flex-1" />
                    </div>
                  </>
                )}
                <form onSubmit={doRegister} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="reg-name">Name</Label>
                    <Input id="reg-name" required value={regForm.name}
                      onChange={(e) => setRegForm({ ...regForm, name: e.target.value })}
                      placeholder="Alex Trader" data-testid="register-name-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="reg-email">Email</Label>
                    <Input id="reg-email" type="email" required value={regForm.email}
                      onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                      placeholder="you@example.com" data-testid="register-email-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="reg-password">Password</Label>
                    <Input id="reg-password" type="password" required minLength={8} value={regForm.password}
                      onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
                      placeholder="At least 8 characters" data-testid="register-password-input" />
                  </div>
                  <Button type="submit" className="w-full" disabled={busy} data-testid="register-submit-button">
                    {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create account
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}
